import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { PrismComboSelectInput } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import {
  BENI_COMPARISON_SETS,
  type ComparisonSet,
  type ComparisonSetWithoutOwn,
  isComparisonSetWithoutOwn,
} from 'frontend-utils/utils/constants/benchmark-insight';
import {
  DEFAULT_MARKET_COMPOSITION_TAB,
  MARKET_COMPOSITION_TABS,
} from 'lumen/controllers/hotel/benchmark-insight/market-composition';
import type { RouterServiceWithLegacyTransitionTo } from 'lumen/initializers/router-legacy-transition-to';
import { BENI_COMPARISON_SET_LONG_TRANSLATIONS } from 'lumen/utils/constants/benchmark-insight/beni-constant-values';
import {
  DROPDOWN_COMPARISON_SETS_OPTION_NONE,
  DROPDOWN_COMPARISON_SETS_OPTION_NONE_TRANSLATION,
  type DropdownComparisonSetsOptionNone,
  type DropdownPrimaryComparisonSetOption,
  type DropdownSecondaryComparisonSetOption,
} from 'lumen/utils/constants/benchmark-insight/comparison-set-selection/dropdown-comparison-sets';

interface HotelBenchmarkInsightDropdownsDropdownComparisonSetsSignature {
  Args: {
    primaryComparisonSet: ComparisonSetWithoutOwn | null;
    secondaryComparisonSet:
      | ComparisonSetWithoutOwn
      | DropdownComparisonSetsOptionNone;
    setPrimaryComparisonSet: (comparisonSet: ComparisonSetWithoutOwn) => void;
    setSecondaryComparisonSet: (
      comparisonSet: ComparisonSetWithoutOwn | DropdownComparisonSetsOptionNone,
    ) => void;
  };
}

export default class HotelBenchmarkInsightDropdownsDropdownComparisonSets extends Component<HotelBenchmarkInsightDropdownsDropdownComparisonSetsSignature> {
  @service declare access: Services['access'];
  @service('benchmark-insight/beni-service')
  declare benchmarkInsightBeniService: Services['benchmark-insight/beni-service'];
  @service('benchmark-insight/features')
  declare benchmarkInsightFeatures: Services['benchmark-insight/features'];
  @service('benchmark-insight/beni-hotels')
  declare beniHotelsService: Services['benchmark-insight/beni-hotels'];
  @service declare intl: Services['intl'];
  @service declare router: RouterServiceWithLegacyTransitionTo;
  @service declare usageAnalytics: Services['usage-analytics'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get additionalAction() {
    return {
      label: this.intl.t(
        'benchmark_insight.market_composition.view_composition',
      ),
      dataTestId: 'additional-action-view-data-report',
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get additionalActionPrimary() {
    const { primaryComparisonSet } = this.args;

    const enabledPrimaryComparisonSetOptions =
      this.primaryComparisonSetOptions.filter((option) => !option.disabled);

    if (
      !this.showAdditionalActionViewDataReport ||
      !enabledPrimaryComparisonSetOptions.length
    ) {
      return undefined;
    }

    return {
      ...this.additionalAction,
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      onClick: () => this.navigateToMarketComposition(primaryComparisonSet),
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get additionalActionSecondary() {
    const { secondaryComparisonSet } = this.args;

    const enabledSecondaryComparisonSetOptions =
      this.secondaryComparisonSetOptions.filter(
        (option) =>
          !option.disabled &&
          option.id !== DROPDOWN_COMPARISON_SETS_OPTION_NONE,
      );

    if (
      !this.showAdditionalActionViewDataReport ||
      !enabledSecondaryComparisonSetOptions.length
    ) {
      return undefined;
    }

    return {
      ...this.additionalAction,
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      onClick: () => this.navigateToMarketComposition(secondaryComparisonSet),
    };
  }

  // We add disabled options to the dropdown, to show the user that the option would be available if they upsell.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get benchmarkInsightLightDisabledOptions() {
    const comparisonSets = [
      BENI_COMPARISON_SETS.COMPSET,
      BENI_COMPARISON_SETS.SMART_COMPSET,
    ];

    const options: DropdownPrimaryComparisonSetOption[] = comparisonSets.map(
      (comparisonSet) => this.mapComparisonSetToDropdownOption(comparisonSet),
    );

    options.forEach((option) => {
      option.disabled = true;
      option.icon = 'rocket';
      option.disabledMessage =
        this.benchmarkInsightFeatures.beniLightTooltipText;
    });

    return options;
  }

  // When compset is available for the subscription, but no compset has been set up, we show a disabled option with a badge.
  get compsetDisabledOption(): DropdownPrimaryComparisonSetOption {
    const optionCompset = this.mapComparisonSetToDropdownOption(
      BENI_COMPARISON_SETS.COMPSET,
    );

    optionCompset.disabled = true;
    optionCompset.badge = this.intl.t(
      'benchmark_insight.data_set_selection.compset.badge',
    );
    optionCompset.badgeType = 'neutral';
    optionCompset.disabledMessage = this.intl.t(
      'benchmark_insight.data_set_selection.compset.tooltip',
    );

    return optionCompset;
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  get primaryComparisonSetOptions(): DropdownPrimaryComparisonSetOption[] {
    const { allowedComparisonSets } = this.benchmarkInsightBeniService;
    const { showTrialTeasers } = this.benchmarkInsightFeatures;

    const options = allowedComparisonSets.map((comparisonSet) =>
      this.mapComparisonSetToDropdownOption(comparisonSet),
    );

    if (showTrialTeasers) {
      options.push(...this.benchmarkInsightLightDisabledOptions);
    }

    if (this.showDisabledCompsetOption) {
      // Add the compset option at the beginning of the list because it's the most important data set.
      options.unshift(this.compsetDisabledOption);
    }

    return options;
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  get secondaryComparisonSetOptions(): DropdownSecondaryComparisonSetOption[] {
    const { allowedComparisonSets } = this.benchmarkInsightBeniService;
    const { showTrialTeasers } = this.benchmarkInsightFeatures;

    const options: DropdownSecondaryComparisonSetOption[] =
      allowedComparisonSets
        .filter(
          (comparisonSet) => comparisonSet !== this.args.primaryComparisonSet,
        )
        .map((comparisonSet) =>
          this.mapComparisonSetToDropdownOption(comparisonSet),
        );

    if (showTrialTeasers) {
      options.push(...this.benchmarkInsightLightDisabledOptions);
    }

    if (this.showDisabledCompsetOption) {
      // Add the compset option at the beginning of the list because it's the most important data set.
      options.unshift(this.compsetDisabledOption);
    }

    options.push({
      id: DROPDOWN_COMPARISON_SETS_OPTION_NONE,
      label: this.intl.t(DROPDOWN_COMPARISON_SETS_OPTION_NONE_TRANSLATION),
    });

    return options;
  }

  get selectedPrimaryComparisonSetOption(): DropdownPrimaryComparisonSetOption | null {
    return (
      this.primaryComparisonSetOptions.find(
        (option) => option.id === this.args.primaryComparisonSet,
      ) ?? null
    );
  }

  get selectedSecondaryComparisonSetOption(): DropdownSecondaryComparisonSetOption | null {
    return (
      this.secondaryComparisonSetOptions.find(
        (option) => option.id === this.args.secondaryComparisonSet,
      ) ?? null
    );
  }

  get showAdditionalActionViewDataReport(): boolean {
    return this.benchmarkInsightBeniService.allowedComparisonSets.some(
      (comparisonSet) => MARKET_COMPOSITION_TABS.includes(comparisonSet),
    );
  }

  // Show the disabled "Compset Not set up" option only once we know the hotel's
  // available comparison sets. While the /hotels endpoint is still loading,
  // availableComparisonSets is [] which would incorrectly show the option.
  get showDisabledCompsetOption(): boolean {
    return (
      !this.beniHotelsService.isLoading &&
      this.access.hasBenchmarkInsightCompset &&
      !this.beniHotelsService.availableComparisonSets.includes(
        BENI_COMPARISON_SETS.COMPSET,
      )
    );
  }

  mapComparisonSetToDropdownOption(
    comparisonSet: ComparisonSetWithoutOwn,
  ): DropdownPrimaryComparisonSetOption {
    const option: DropdownPrimaryComparisonSetOption = {
      id: comparisonSet,
      label: this.intl.t(BENI_COMPARISON_SET_LONG_TRANSLATIONS[comparisonSet]),
    };

    return option;
  }

  sendUsageTrackingEvent(
    comparisonSet: ComparisonSet | DropdownComparisonSetsOptionNone,
    primary: boolean,
  ): void {
    this.usageAnalytics.track('Data Set Selected', {
      eventTrigger: 'header',
      type: comparisonSet,
      primary,
    });
  }

  @action
  navigateToMarketComposition(
    activeTab:
      | ComparisonSetWithoutOwn
      | DropdownComparisonSetsOptionNone
      | null,
  ): void {
    let activeTabWithDefault = activeTab;

    // Set a default tab. There is QP validation in the transition service that also handles this, but the validation is causing the benchmark-insight route model to refresh
    // if we pass an invalid tab as `activeTab` QP.
    if (
      activeTabWithDefault &&
      (!isComparisonSetWithoutOwn(activeTabWithDefault) ||
        !MARKET_COMPOSITION_TABS.includes(activeTabWithDefault))
    ) {
      // If the active tab is not one of the available market composition tabs, set it to the first available tab.
      activeTabWithDefault =
        MARKET_COMPOSITION_TABS.find((tab) =>
          this.benchmarkInsightBeniService.allowedComparisonSets.includes(tab),
        ) ?? DEFAULT_MARKET_COMPOSITION_TAB;
    }

    // Use `legacyTransitionTo` to prevent the benchmark-insight route model hook from re-running.
    // The market composition route is a modal shown on top of the benchmark-insight route template.
    // We don't want the components to load again in the background of the modal.
    this.router.legacyTransitionTo(
      'hotel.benchmark-insight.market-composition',
      {
        queryParams: {
          activeTab: activeTabWithDefault,
        },
      },
    );

    this.usageAnalytics.track('Data Composition Report Opened', {
      product: 'benchmark-insight',
      tab: activeTabWithDefault,
      eventTrigger: 'dropdown-comparison-sets',
    });
  }

  @action
  onChangePrimaryComparisonSet(
    option: DropdownPrimaryComparisonSetOption,
  ): void {
    const {
      setPrimaryComparisonSet,
      setSecondaryComparisonSet,
      secondaryComparisonSet,
    } = this.args;
    const comparisonSet = option.id;

    // If the user selects the selected secondary comparison set as primary comparison set, switch the two values in the dropdown.
    if (secondaryComparisonSet === comparisonSet) {
      setSecondaryComparisonSet(DROPDOWN_COMPARISON_SETS_OPTION_NONE);
    }

    this.sendUsageTrackingEvent(comparisonSet, true);

    setPrimaryComparisonSet(comparisonSet);
  }

  @action
  onChangeSecondaryComparisonSet(
    option: DropdownSecondaryComparisonSetOption,
  ): void {
    const { setSecondaryComparisonSet } = this.args;

    const comparisonSet = option.id;

    this.sendUsageTrackingEvent(comparisonSet, false);

    setSecondaryComparisonSet(comparisonSet);
  }

  <template>
    <div class="ic-product-tour-smart-compset-dropdown">
      <PrismComboSelectInput
        @disabled={{this.beniHotelsService.isLoading}}
        @disabledMessage={{if
          this.beniHotelsService.isLoading
          (t "general.filter_loading")
        }}
        @onChange={{this.onChangePrimaryComparisonSet}}
        @options={{this.primaryComparisonSetOptions}}
        @placeholder={{t
          "benchmark_insight.data_set_selection.placeholder.primary_data_set"
        }}
        @unsafe_additionalAction={{this.additionalActionPrimary}}
        @value={{this.selectedPrimaryComparisonSetOption}}
        data-test-id="dropdown-comparison-set-primary"
      />
    </div>

    <PrismComboSelectInput
      @disabled={{this.beniHotelsService.isLoading}}
      @disabledMessage={{if
        this.beniHotelsService.isLoading
        (t "general.filter_loading")
      }}
      @onChange={{this.onChangeSecondaryComparisonSet}}
      @options={{this.secondaryComparisonSetOptions}}
      @placeholder={{t
        "benchmark_insight.data_set_selection.placeholder.secondary_data_set"
      }}
      @unsafe_additionalAction={{this.additionalActionSecondary}}
      @value={{this.selectedSecondaryComparisonSetOption}}
      data-test-id="dropdown-comparison-set-secondary"
    />
  </template>
}
